import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button, DatePicker, SelectSearch, Text } from '@mercai/clever'

import { CardOrderSelectSupplierProps } from './card-order-select-supplier.type'

import styles from './card-order-select-supplier.module.css'
import { SupplierProps } from '@/types'
import { useValidationErrors } from '@/helpers'
import { schema } from './card-order-select-supplier.constants'
import { useSupplierList } from '@/services'

export const CardOrderSelectSupplier = ({
  supplier,
  supplierPaidAt,
  onSubmit,
  onCancel,
  isLoading,
}: CardOrderSelectSupplierProps) => {
  const { t } = useTranslation('componentCardOrderSelectSupplier')

  const [internalSupplier, setInternalSupplier] = useState(supplier)
  const [internalSupplierPaidAt, setInternalSupplierPaidAt] = useState(
    supplierPaidAt || undefined,
  )

  const [supplierSearch, setSupplierSearch] = useState<string>('')

  const { data, isLoading: isLoadingSupplierList } = useSupplierList({
    params: { search: supplierSearch },
  })

  const { isValid, errors, onHandleListenErrors } = useValidationErrors(
    schema(t),
    {
      supplierUuid: internalSupplier?.uuid,
    },
  )

  const handleSelectSupplier = (supp: Partial<SupplierProps>) => {
    setInternalSupplier(supp as SupplierProps)
  }

  const handleSubmit = () => {
    if (!isValid) {
      onHandleListenErrors(true)
      return
    }

    onHandleListenErrors(false)
    onSubmit(internalSupplier || {}, internalSupplierPaidAt || '')
  }

  useEffect(() => {
    setInternalSupplier(supplier)
    setInternalSupplierPaidAt(supplierPaidAt)
  }, [supplier, supplierPaidAt, onSubmit, onCancel])

  return (
    <div className={styles.content}>
      <Text variant="text-medium">
        <strong>{t('title')}</strong>
      </Text>

      <div>
        <div className={styles.form}>
          <div>
            <Text variant="text-base" className={styles.label}>
              {t('formSupplierLabel')} <span>*</span>
            </Text>
            <SelectSearch<SupplierProps>
              hasLabel={false}
              dataSearch={data?.data?.results || []}
              isLoadingSearch={isLoadingSupplierList}
              onSearch={setSupplierSearch}
              onSelectItem={handleSelectSupplier}
              selectedItem={internalSupplier}
              keyLabel="name"
              keyValue="uuid"
              translations={{
                inputSearchLabel: t('formSupplierLabel'),
                inputSearchPlaceholder: t('formSupplierPlaceholder'),
                noResults: t('formSupplierNoResults'),
              }}
              error={errors?.supplierUuid as string}
            />
          </div>

          <div>
            <Text variant="text-base" className={styles.label}>
              {t('formSupplyPaymentDateLabel')}
            </Text>
            <DatePicker
              placeholder={t('formSupplyPaymentDatePlaceholder')}
              value={internalSupplierPaidAt || undefined}
              onChange={(newValue) => setInternalSupplierPaidAt(newValue)}
            />
          </div>
        </div>

        <div className={styles.actions}>
          <Button variant="secondary" onClick={onCancel}>
            {t('cancelButton')}
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={isLoading || isLoadingSupplierList}
          >
            {t('confirmButton')}
          </Button>
        </div>
      </div>
    </div>
  )
}
